import React from 'react';
import { 
  BarChart3, 
  Target, 
  Zap, 
  Users, 
  Globe, 
  LineChart,
  Bot,
  Layers,
  Sparkles,
  Lightbulb,
  Clock,
  RefreshCw,
  DollarSign,
  AlertCircle
} from 'lucide-react';

export default function MarketingChannels() {
  return (
    <div className="py-4 bg-white relative overflow-hidden">
      <style jsx global>{`
        @keyframes orbit {
          0% {
            transform: rotate(0deg) translateX(80px) rotate(0deg);
          }
          100% {
            transform: rotate(360deg) translateX(80px) rotate(-360deg);
          }
        }
        .animate-orbit {
          animation: orbit 20s linear infinite;
        }
        @keyframes float1 {
          0% { transform: translate(0, 0); }
          25% { transform: translate(30px, -20px); }
          50% { transform: translate(-10px, -40px); }
          75% { transform: translate(-40px, -10px); }
          100% { transform: translate(0, 0); }
        }
        
        @keyframes float2 {
          0% { transform: translate(0, 0); }
          25% { transform: translate(-30px, 20px); }
          50% { transform: translate(20px, 40px); }
          75% { transform: translate(40px, 10px); }
          100% { transform: translate(0, 0); }
        }
        
        @keyframes float3 {
          0% { transform: translate(0, 0); }
          25% { transform: translate(40px, 30px); }
          50% { transform: translate(10px, -30px); }
          75% { transform: translate(-30px, 20px); }
          100% { transform: translate(0, 0); }
        }
        
        @keyframes float4 {
          0% { transform: translate(0, 0); }
          25% { transform: translate(-20px, -40px); }
          50% { transform: translate(-40px, 10px); }
          75% { transform: translate(10px, 40px); }
          100% { transform: translate(0, 0); }
        }
        
        @keyframes float5 {
          0% { transform: translate(0, 0); }
          25% { transform: translate(10px, 40px); }
          50% { transform: translate(40px, -10px); }
          75% { transform: translate(-10px, -30px); }
          100% { transform: translate(0, 0); }
        }
        
        @keyframes float6 {
          0% { transform: translate(0, 0); }
          25% { transform: translate(-40px, -10px); }
          50% { transform: translate(-10px, 40px); }
          75% { transform: translate(30px, -20px); }
          100% { transform: translate(0, 0); }
        }
        
        .float-1 { animation: float1 15s ease-in-out infinite; }
        .float-2 { animation: float2 18s ease-in-out infinite; }
        .float-3 { animation: float3 20s ease-in-out infinite; }
        .float-4 { animation: float4 17s ease-in-out infinite; }
        .float-5 { animation: float5 22s ease-in-out infinite; }
        .float-6 { animation: float6 19s ease-in-out infinite; }
      `}</style>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-base font-semibold text-blue-600 tracking-wide uppercase mb-1">
            Transform Your Marketing
          </h2>
          <p className="text-2xl font-bold text-gray-900 sm:text-3xl">
            Powerful Features to Elevate Your Strategy
          </p>
          <p className="mt-3 text-base text-gray-600 max-w-2xl mx-auto">
            Discover how our AI-powered platform can help you achieve better marketing results
          </p>
        </div>

        {/* Grid layout similar to the example image */}
        <div className="grid grid-cols-12 gap-5">
          {/* First row */}
          <div className="col-span-8 bg-blue-50 rounded-3xl p-8 min-h-[280px]">
            <div className="flex">
              <div className="w-1/3">
                <h3 className="text-2xl font-semibold text-gray-900 mb-3">Cross-Platform Integration</h3>
                <p className="text-gray-600 mb-6">Discover hidden insights by connecting all your marketing channels in one place. See the complete picture of your customer journey across platforms.</p>
              </div>
              
              <div className="w-2/3 relative">
                {/* Central Glowytics logo with pulsing animation */}
                <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                  <div className="relative">
                    <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-md">
                      <img 
                        src="/logoicon.png" 
                        alt="Glowytics" 
                        className="w-14 h-14 object-contain"
                      />
                    </div>
                    <div className="absolute inset-0 bg-blue-500 rounded-full animate-ping opacity-10"></div>
                  </div>
                </div>
                
                {/* Floating logos */}
                <div className="absolute inset-0">
                  {/* Google Ads */}
                  <div className="absolute w-12 h-12 left-[30%] top-[25%] float-1">
                    <img 
                      src="/google_ads-512.webp" 
                      alt="Google Ads" 
                      className="w-full h-full object-contain rounded-full bg-white p-1 shadow-md"
                    />
                  </div>
                  
                  {/* Google Analytics */}
                  <div className="absolute w-12 h-12 left-[65%] top-[20%] float-2">
                    <img 
                      src="/GoogleAnalyticslogo.png" 
                      alt="Google Analytics" 
                      className="w-full h-full object-contain rounded-full bg-white p-1 shadow-md"
                    />
                  </div>
                  
                  {/* Meta */}
                  <div className="absolute w-12 h-12 left-[20%] top-[50%] float-3">
                    <img 
                      src="/6033716.png" 
                      alt="Meta" 
                      className="w-full h-full object-contain rounded-full bg-white p-1 shadow-md"
                    />
                  </div>
                  
                  {/* TikTok */}
                  <div className="absolute w-12 h-12 left-[75%] top-[45%] float-4">
                    <img 
                      src="/download.png" 
                      alt="TikTok" 
                      className="w-full h-full object-contain rounded-full bg-white p-1 shadow-md"
                    />
                  </div>
                  
                  {/* RTB House */}
                  <div className="absolute w-12 h-12 left-[25%] top-[75%] float-5">
                    <img 
                      src="/29374191.png" 
                      alt="RTB House" 
                      className="w-full h-full object-contain rounded-full bg-white p-1 shadow-md"
                    />
                  </div>
                  
                  {/* Criteo */}
                  <div className="absolute w-12 h-12 left-[70%] top-[70%] float-6">
                    <img 
                      src="/CRTO-25f99ae9.png" 
                      alt="Criteo" 
                      className="w-full h-full object-contain rounded-full bg-white p-1 shadow-md"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-span-4 row-span-2 bg-blue-50 rounded-3xl p-8 min-h-[570px] flex flex-col">
            {/* Badge */}
            <div className="inline-flex items-center px-3 py-1.5 bg-blue-100 rounded-full text-sm font-medium text-blue-700 mb-4">
              <svg className="w-4 h-4 mr-1.5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M12 4L20 8.00004L12 12L4 8.00004L12 4Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M20 16.0001L12 20.0001L4 16.0001" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M20 12L12 16.0001L4 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              AI ASSISTANT
            </div>
            
            {/* Başlık */}
            <h3 className="text-3xl font-bold text-gray-900 mb-3">
              Meet Your Marketing <span className="text-blue-600">Co-Pilot</span>
            </h3>
            
            {/* Açıklama */}
            <p className="text-gray-600 mb-8 text-lg">
              Manage and optimize your campaigns through natural conversations with our AI assistant.
            </p>
            
            {/* Özellikler */}
            <div className="grid grid-cols-1 gap-y-6 mb-8">
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <p className="text-gray-900 font-medium">Intelligent campaign optimization</p>
                  <p className="text-gray-600 text-sm">Automatically identify opportunities to improve performance</p>
                </div>
              </div>
              
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-xl bg-purple-600 flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-gray-900 font-medium">Real-time performance insights</p>
                  <p className="text-gray-600 text-sm">Get instant alerts and actionable insights for campaigns</p>
                </div>
              </div>
              
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-xl bg-indigo-500 flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <div>
                  <p className="text-gray-900 font-medium">Automated budget recommendations</p>
                  <p className="text-gray-600 text-sm">Optimize spend allocation based on performance data</p>
                </div>
              </div>
            </div>
            
            {/* Robot */}
            <div className="flex-grow flex items-end justify-center">
              <img 
                src="/14rob.png" 
                alt="GlowyAI Assistant" 
                className="max-h-[280px] w-auto object-contain"
              />
            </div>
          </div>

          {/* Second row */}
          <div className="col-span-4 bg-purple-50 rounded-3xl p-8 min-h-[280px] flex flex-col justify-end">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Advanced Analytics</h3>
            <p className="text-gray-600 mb-4 text-sm">Get deep insights into your marketing performance with beautiful visualizations.</p>
            <img 
              src="/09-Analytics.gif" 
              alt="Advanced Analytics" 
              className="w-full mt-auto"
            />
          </div>

          <div className="col-span-4 row-span-2 bg-indigo-50 rounded-3xl p-8 min-h-[570px] flex flex-col">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Cross-Channel Attribution</h3>
              <p className="text-gray-600 mb-6 text-sm">Understand which channels drive conversions with multi-touch attribution models.</p>
            </div>
            <div className="mt-4 flex-grow relative">
              <div className="h-full flex flex-col items-center justify-between">
                {/* Top row - source channels */}
                <div className="flex justify-between w-full mb-6">
                  <div className="flex flex-col items-center group">
                    <div className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center mb-2 transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg">
                      <img src="/google_ads-512.webp" alt="Google Ads" className="w-8 h-8 object-contain" />
                    </div>
                    <span className="text-xs text-gray-600 opacity-100 transition-opacity duration-300 group-hover:text-blue-600 group-hover:font-medium">Google Ads</span>
                    <div className="absolute opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white p-2 rounded-md shadow-md text-xs text-gray-700 w-32 text-center mt-16 z-20">
                      Drives 35% of first touches
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-center group">
                    <div className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center mb-2 transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg">
                      <img src="/6033716.png" alt="Meta" className="w-8 h-8 object-contain" />
                    </div>
                    <span className="text-xs text-gray-600 opacity-100 transition-opacity duration-300 group-hover:text-blue-600 group-hover:font-medium">Meta Ads</span>
                    <div className="absolute opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white p-2 rounded-md shadow-md text-xs text-gray-700 w-32 text-center mt-16 z-20">
                      Strongest for middle touches
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-center group">
                    <div className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center mb-2 transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg">
                      <img src="/download.png" alt="TikTok" className="w-8 h-8 object-contain" />
                    </div>
                    <span className="text-xs text-gray-600 opacity-100 transition-opacity duration-300 group-hover:text-blue-600 group-hover:font-medium">TikTok Ads</span>
                    <div className="absolute opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white p-2 rounded-md shadow-md text-xs text-gray-700 w-32 text-center mt-16 z-20">
                      Great for awareness stage
                    </div>
                  </div>
                </div>
                
                {/* Connection lines */}
                <div className="relative w-full h-32 my-4">
                  <div className="absolute left-[16.6%] top-0 w-1 h-full bg-gradient-to-b from-blue-300 to-purple-400 transition-all duration-500 hover:w-2"></div>
                  <div className="absolute left-[50%] top-0 w-1 h-full bg-gradient-to-b from-blue-300 to-purple-400 transition-all duration-500 hover:w-2"></div>
                  <div className="absolute left-[83.3%] top-0 w-1 h-full bg-gradient-to-b from-blue-300 to-purple-400 transition-all duration-500 hover:w-2"></div>
                  
                  <div className="absolute left-0 right-0 top-[50%] h-1 bg-gradient-to-r from-blue-300 via-purple-400 to-blue-300 transition-all duration-500 hover:h-2"></div>
                  
                  {/* Central attribution model */}
                  <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-white rounded-full shadow-lg flex items-center justify-center z-10 border-2 border-purple-100 transition-all duration-300 hover:scale-110 hover:shadow-xl cursor-pointer group">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center transition-transform duration-300 group-hover:rotate-45">
                      <Layers className="w-8 h-8 text-white transition-transform duration-300 group-hover:rotate-[-45deg]" />
                    </div>
                    <div className="absolute opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white p-2 rounded-md shadow-md text-xs text-gray-700 w-40 text-center mt-32 z-20">
                      Multi-touch attribution model
                    </div>
                  </div>
                </div>
                
                {/* Touch points */}
                <div className="w-full mb-6">
                  <div className="bg-white rounded-lg shadow-md p-4 mb-4 transition-all duration-300 hover:shadow-lg hover:translate-x-1 cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3 transition-all duration-300 hover:bg-blue-200">
                          <span className="text-blue-600 font-medium">1</span>
                        </div>
                        <span className="text-sm font-medium">First Touch</span>
                      </div>
                      <div className="text-sm text-gray-600">25% Attribution</div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-lg shadow-md p-4 mb-4 transition-all duration-300 hover:shadow-lg hover:translate-x-1 cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mr-3 transition-all duration-300 hover:bg-purple-200">
                          <span className="text-purple-600 font-medium">2</span>
                        </div>
                        <span className="text-sm font-medium">Middle Touch</span>
                      </div>
                      <div className="text-sm text-gray-600">35% Attribution</div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-lg shadow-md p-4 transition-all duration-300 hover:shadow-lg hover:translate-x-1 cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center mr-3 transition-all duration-300 hover:bg-indigo-200">
                          <span className="text-indigo-600 font-medium">3</span>
                        </div>
                        <span className="text-sm font-medium">Last Touch</span>
                      </div>
                      <div className="text-sm text-gray-600">40% Attribution</div>
                    </div>
                  </div>
                </div>
                
                {/* Conversion */}
                <div className="flex flex-col items-center group">
                  <div className="w-14 h-14 rounded-full bg-green-100 shadow-md flex items-center justify-center mb-2 transition-all duration-500 group-hover:scale-110 group-hover:shadow-lg cursor-pointer">
                    <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center transition-all duration-500 group-hover:bg-green-600">
                      <Target className="w-6 h-6 text-white transition-transform duration-500 group-hover:rotate-180" />
                    </div>
                  </div>
                  <span className="text-sm font-medium text-green-700 group-hover:text-green-800">Conversion</span>
                  <div className="absolute opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white p-2 rounded-md shadow-md text-xs text-gray-700 w-40 text-center mt-16 z-20">
                    $45 average conversion value
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Third row */}
          <div className="col-span-4 bg-green-50 rounded-3xl p-8 min-h-[280px]">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Automated Reporting</h3>
            <p className="text-gray-600 mb-4 text-sm">Schedule and automate comprehensive reports delivered to your inbox.</p>
            <div className="flex justify-center items-center h-32">
              <div className="relative w-48 h-32">
                {/* Main report card */}
                <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-36 h-44 bg-white rounded-lg shadow-md flex flex-col items-center justify-center z-20">
                  <div className="mt-4 text-blue-500">
                    <BarChart3 className="w-8 h-8 mx-auto" />
                  </div>
                  <div className="w-20 h-2 bg-blue-100 rounded mt-3 mb-2"></div>
                  <div className="w-16 h-2 bg-blue-100 rounded mb-2"></div>
                  <div className="w-20 h-2 bg-blue-100 rounded"></div>
                  <div className="mt-3 flex space-x-2">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                      <LineChart className="w-3 h-3 text-green-500" />
                    </div>
                    <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center">
                      <BarChart3 className="w-3 h-3 text-purple-500" />
                    </div>
                  </div>
                </div>
                
                {/* Clock icon */}
                <div className="absolute top-0 right-0 w-10 h-10 bg-white rounded-full shadow-sm border border-gray-100 flex items-center justify-center z-30">
                  <Clock className="w-5 h-5 text-blue-500" />
                </div>
                
                {/* Refresh icon */}
                <div className="absolute bottom-2 left-0 w-10 h-10 bg-white rounded-full shadow-sm border border-gray-100 flex items-center justify-center z-30">
                  <RefreshCw className="w-5 h-5 text-green-500" />
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-span-4 bg-blue-50 rounded-3xl p-8 min-h-[280px]">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Audience Segmentation</h3>
            <p className="text-gray-600 mb-6 text-sm">Target the right customers with AI-powered audience segmentation and insights.</p>
            
            <div className="flex justify-center items-center relative h-24 mb-6">
              {/* Central user icon */}
              <div className="w-14 h-14 rounded-full bg-white shadow-md flex items-center justify-center z-10">
                <Users className="w-7 h-7 text-blue-500" />
              </div>
              
              {/* Segment circles - only 3 main ones */}
              <div className="absolute left-[20%] top-1/2 transform -translate-y-1/2">
                <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center shadow-md">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div className="absolute -bottom-4 left-0 right-0 text-center">
                  <span className="text-xs font-medium text-gray-700">New</span>
                </div>
              </div>
              
              <div className="absolute left-[30%] -bottom-2">
                <div className="w-10 h-10 rounded-full bg-amber-400 flex items-center justify-center shadow-md">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
                <div className="absolute -bottom-4 left-0 right-0 text-center">
                  <span className="text-xs font-medium text-gray-700">High Value</span>
                </div>
              </div>
              
              <div className="absolute right-[20%] top-1/2 transform -translate-y-1/2">
                <div className="w-12 h-12 rounded-full bg-red-500 flex items-center justify-center shadow-md">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div className="absolute -bottom-4 left-0 right-0 text-center">
                  <span className="text-xs font-medium text-gray-700">At Risk</span>
                </div>
              </div>
              
              {/* Simple connection lines */}
              <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
                <line x1="50%" y1="50%" x2="20%" y2="50%" stroke="#3B82F6" strokeWidth="2" strokeDasharray="4" />
                <line x1="50%" y1="50%" x2="30%" y2="70%" stroke="#3B82F6" strokeWidth="2" strokeDasharray="4" />
                <line x1="50%" y1="50%" x2="80%" y2="50%" stroke="#3B82F6" strokeWidth="2" strokeDasharray="4" />
              </svg>
            </div>
            
            {/* Stats bar - simplified */}
            <div className="grid grid-cols-4 gap-2 text-center border-t border-blue-100 pt-4">
              <div>
                <p className="text-xs text-gray-500">Segments</p>
                <p className="text-sm font-semibold text-gray-900">12+</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Accuracy</p>
                <p className="text-sm font-semibold text-gray-900">94%</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Conversion</p>
                <p className="text-sm font-semibold text-green-600">+68%</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">ROI</p>
                <p className="text-sm font-semibold text-green-600">+125%</p>
              </div>
            </div>
          </div>
          

        </div>
        
   
      </div>
    </div>
  );
} 
